import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, ArrowLeft, TrendingUp, Store, Users, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const steps = ["Contact & Location", "Investment & Store", "Experience & Timeline"];

export default function Franchise() {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState({
    full_name: "", email: "", phone: "", city: "", state: "",
    investment_capacity: "", store_size_sqft: "", experience: "", timeline: "", message: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const validStep = () => {
    if (step === 0) return f.full_name && f.email && f.phone && f.city && f.state;
    if (step === 1) return f.investment_capacity && f.store_size_sqft;
    return f.experience && f.timeline;
  };

  const next = () => { if (!validStep()) { toast.error("Please complete all fields"); return; } setStep((s) => Math.min(s + 1, 2)); };

  const submit = async () => {
    if (!validStep()) { toast.error("Please complete all fields"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/enquiries", f);
      setDone(data);
      toast.success("Application submitted!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="franchise-success">
        <CheckCircle2 className="w-16 h-16 text-yellow-600 mx-auto" />
        <h1 className="font-heading text-3xl font-bold mt-4">Application Submitted!</h1>
        <p className="text-muted-foreground mt-2">Our franchise team will review and reach out within 3-5 business days.</p>
        <p className="mt-6 text-sm text-muted-foreground">Your tracking reference</p>
        <p className="font-mono text-xl font-bold text-yellow-700 bg-yellow-100 dark:bg-yellow-950 inline-block px-6 py-3 rounded-xl mt-2">{done.reference}</p>
        <div className="mt-8">
          <Button asChild className="bg-yellow-400 hover:bg-yellow-500 text-slate-900"><a href="/track">Track Application</a></Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="bg-yellow-400 text-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-800">Franchise Opportunity</span>
            <h1 className="font-heading text-4xl lg:text-5xl font-extrabold mt-2">Own a D K BAZAAR</h1>
            <p className="mt-4 text-slate-800 text-lg max-w-md">A proven grocery retail model with central supply chain, brand marketing, and complete onboarding support.</p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              {[[TrendingUp, "22%", "Avg. ROI"], [Store, "6 mo", "To launch"], [Users, "40+", "Franchisees"]].map(([Icon, n, l], i) => (
                <div key={i} className="bg-slate-900/10 rounded-xl p-4">
                  <Icon className="w-5 h-5 text-slate-800" />
                  <p className="font-heading font-bold text-2xl mt-2">{n}</p>
                  <p className="text-xs text-slate-700">{l}</p>
                </div>
              ))}
            </div>
          </div>
          <img src="https://images.unsplash.com/photo-1578916171728-46686eac8d58?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
            alt="Modern retail aisle" className="rounded-2xl object-cover w-full h-72 lg:h-96" />
        </div>
      </section>

      <section className="max-w-2xl mx-auto px-4 sm:px-8 py-14">
        <div className="mb-8">
          <div className="flex justify-between text-sm mb-2">
            {steps.map((s, i) => (
              <span key={s} className={`font-medium ${i <= step ? "text-yellow-700" : "text-muted-foreground"}`}>
                {i < step ? <Check className="w-4 h-4 inline" /> : `${i + 1}.`} {s}
              </span>
            ))}
          </div>
          <Progress value={((step + 1) / 3) * 100} className="h-2" />
        </div>

        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8">
          {step === 0 && (
            <>
              <div><Label>Full Name</Label><Input data-testid="fr-name" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Email</Label><Input data-testid="fr-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
                <div><Label>Phone</Label><Input data-testid="fr-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>City</Label><Input data-testid="fr-city" value={f.city} onChange={(e) => set("city", e.target.value)} /></div>
                <div><Label>State</Label><Input data-testid="fr-state" value={f.state} onChange={(e) => set("state", e.target.value)} /></div>
              </div>
            </>
          )}
          {step === 1 && (
            <>
              <div>
                <Label>Investment Capacity</Label>
                <Select value={f.investment_capacity} onValueChange={(v) => set("investment_capacity", v)}>
                  <SelectTrigger data-testid="fr-investment"><SelectValue placeholder="Select range" /></SelectTrigger>
                  <SelectContent>{["₹25-40 Lakh", "₹40-60 Lakh", "₹60 Lakh+"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Preferred Store Size</Label>
                <Select value={f.store_size_sqft} onValueChange={(v) => set("store_size_sqft", v)}>
                  <SelectTrigger data-testid="fr-size"><SelectValue placeholder="Select size" /></SelectTrigger>
                  <SelectContent>{["800-1200 sqft", "1200-2000 sqft", "2000+ sqft"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="rounded-xl bg-yellow-100 dark:bg-yellow-950 p-4 text-sm text-yellow-800 dark:text-yellow-300">
                Estimated setup timeline: <b>4-6 months</b> · Projected break-even: <b>18-24 months</b>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <div>
                <Label>Retail / Business Experience</Label>
                <Select value={f.experience} onValueChange={(v) => set("experience", v)}>
                  <SelectTrigger data-testid="fr-experience"><SelectValue placeholder="Select experience" /></SelectTrigger>
                  <SelectContent>{["No prior experience", "1-3 years in retail", "3-5 years in retail", "5+ years / multi-business"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Expected Timeline to Start</Label>
                <Select value={f.timeline} onValueChange={(v) => set("timeline", v)}>
                  <SelectTrigger data-testid="fr-timeline"><SelectValue placeholder="Select timeline" /></SelectTrigger>
                  <SelectContent>{["Immediately", "3-6 months", "6-12 months", "Just exploring"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Anything else? (optional)</Label><Textarea data-testid="fr-message" value={f.message} onChange={(e) => set("message", e.target.value)} rows={3} /></div>
            </>
          )}

          <div className="flex justify-between pt-4">
            <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="fr-back"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
            {step < 2 ? (
              <Button onClick={next} className="bg-yellow-400 hover:bg-yellow-500 text-slate-900" data-testid="fr-next">Next <ArrowRight className="w-4 h-4 ml-1" /></Button>
            ) : (
              <Button onClick={submit} disabled={submitting} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="fr-submit">{submitting ? "Submitting…" : "Submit Application"}</Button>
            )}
          </div>
        </motion.div>
      </section>
    </div>
  );
}
